import React, { useEffect, useState } from "react";
import { Card, Row, Col, Table, Button, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./admin.css";

import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const API_URL = `${process.env.REACT_APP_API_URL}/api/news`;
const IMAGE_URL = `${process.env.REACT_APP_API_URL}/uploads/`;

const Dashboard = () => {
    const navigate = useNavigate();

    const [stats, setStats] = useState({
        totalNews: 0,
        published: 0,
        draft: 0,
        categories: 0,
        videos: 0,
        ads: 0,
    });

    const [recentNews, setRecentNews] = useState([]);
    const [chartData, setChartData] = useState({});

    useEffect(() => {
        fetchDashboardData();
    }, []);

    // ✅ IMAGE
    const getImage = (item) => {
        if (item?.imagePath) return item.imagePath;
        if (item?.image) return `${IMAGE_URL}${item.image}`;
        return "https://via.placeholder.com/60x40?text=No+Image";
    };

    // ✅ AD IMAGE
    const getAdImage = (item) => {
        if (item?.adImage && item.adImage.startsWith("http"))
            return item.adImage;
        if (item?.adImage) return `${IMAGE_URL}${item.adImage}`;
        return "https://via.placeholder.com/60x40?text=No+Ad";
    };

    // ================= FETCH =================
    const fetchDashboardData = async () => {
        try {
            const res = await fetch(API_URL);
            const result = await res.json();
            const news = result?.data || [];

            const totalNews = news.length;
            const published = news.filter((n) => n.status === "Published").length;
            const draft = news.filter((n) => n.status === "Draft").length;

            // 🎥 VIDEO COUNT
            const videos = news.filter((n) => n.videoUrl).length;

            // 📢 ADS COUNT
            const ads = news.filter((n) => n.adImage).length;

            // CATEGORY MAP
            const categoryMap = {};
            news.forEach((item) => {
                const cat = item.category || "Other";
                categoryMap[cat] = (categoryMap[cat] || 0) + 1;
            });

            // CHART
            setChartData({
                labels: Object.keys(categoryMap),
                datasets: [
                    {
                        label: "News Count",
                        data: Object.values(categoryMap),
                        backgroundColor: [
                            "#dc3545",
                            "#28a745",
                            "#0dcaf0",
                            "#ffc107",
                            "#6c757d",
                            "#6610f2",
                        ],
                        borderRadius: 6,
                    },
                ],
            });

            const recent = [...news].reverse().slice(0, 5);

            setStats({
                totalNews,
                published,
                draft,
                categories: Object.keys(categoryMap).length,
                videos,
                ads,
            });

            setRecentNews(recent);
        } catch (err) {
            console.error("Dashboard Error:", err);
        }
    };

    return (
        <>
            <h3 className="mb-4 fw-bold">📊 Admin Dashboard</h3>

            {/* ================= STATS ================= */}
            <Row className="g-4 mb-4">
                <StatCard title="Total News" value={stats.totalNews} color="danger" icon="📰" />
                <StatCard title="Published" value={stats.published} color="success" icon="✅" />
                <StatCard title="Draft" value={stats.draft} color="secondary" icon="📝" />
                <StatCard title="Categories" value={stats.categories} color="primary" icon="📂" />
                <StatCard title="Videos" value={stats.videos} color="warning" icon="🎥" />
                <StatCard title="Ads" value={stats.ads} color="info" icon="📢" />
            </Row>

            {/* ================= CHART + RECENT ================= */}
            <Row className="g-4 mb-4">

                {/* CHART */}
                <Col md={6}>
                    <Card className="admin-card p-4 shadow-sm">
                        <h5 className="fw-bold mb-3">📊 Category Distribution</h5>

                        {chartData?.labels && (
                            <Bar
                                data={chartData}
                                options={{
                                    responsive: true,
                                    plugins: { legend: { display: false } },
                                }}
                            />
                        )}
                    </Card>
                </Col>

                {/* RECENT NEWS */}
                <Col md={6}>
                    <Card className="admin-card p-4 shadow-sm">
                        <h5 className="fw-bold mb-3">📰 Recent Articles</h5>

                        <Table hover responsive>
                            <thead>
                                <tr>
                                    <th>Img</th>
                                    <th>Title</th>
                                    <th>Video</th>
                                    <th>Ad</th>
                                </tr>
                            </thead>

                            <tbody>
                                {recentNews.map((item, i) => (
                                    <tr key={i}>
                                        <td>
                                            <img
                                                src={getImage(item)}
                                                alt=""
                                                style={{
                                                    width: "60px",
                                                    height: "40px",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </td>

                                        <td>
                                            <strong>{item.title}</strong>
                                            <div style={{ fontSize: "12px" }}>
                                                {item.shortDesc?.slice(0, 30)}...
                                            </div>
                                        </td>

                                        {/* VIDEO */}
                                        <td>
                                            {item.videoUrl ? (
                                                <a href={item.videoUrl} target="_blank" rel="noreferrer">
                                                    ▶
                                                </a>
                                            ) : "-"}
                                        </td>

                                        {/* AD */}
                                        <td>
                                            <img
                                                src={getAdImage(item)}
                                                alt=""
                                                style={{
                                                    width: "50px",
                                                    height: "30px",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                </Col>
            </Row>

            {/* ================= ACTIONS ================= */}
            <Row>
                <Col>
                    <Card className="admin-card p-4 shadow-sm">
                        <h5 className="fw-bold mb-3">⚡ Quick Actions</h5>

                        <div className="d-flex gap-3">
                            <Button variant="danger" onClick={() => navigate("/admin/add-news")}>
                                ➕ Add News
                            </Button>

                            <Button variant="dark" onClick={() => navigate("/admin/manage-news")}>
                                🛠 Manage News
                            </Button>
                        </div>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

// ✅ CARD
const StatCard = ({ title, value, color, icon }) => (
    <Col md={2}>
        <Card className="text-center p-3 shadow-sm">
            <div className={`bg-${color} text-white rounded p-2 mb-2`}>
                {icon}
            </div>
            <h6>{title}</h6>
            <h4 className={`text-${color}`}>{value}</h4>
        </Card>
    </Col>
);

export default Dashboard;